/**
 * CommitteesManagement - Admin component for managing church committees
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Users,
  UserPlus,
  Calendar,
  MapPin,
  Briefcase,
  Home,
  Sparkles,
  Heart,
  Shield,
  DollarSign,
  Settings,
  Building,
  ChevronRight,
  Eye
} from 'lucide-react';

// API functions
const committeesAPI = {
  list: (params) => api.get('/admin/committees', { params }),
  create: (data) => api.post('/admin/committees', data),
  get: (id) => api.get(`/admin/committees/${id}`),
  update: (id, data) => api.put(`/admin/committees/${id}`, data),
  delete: (id) => api.delete(`/admin/committees/${id}`),
  getTypes: () => api.get('/admin/committees/types'),
  getPositions: () => api.get('/admin/committees/positions'),
  getStats: (params) => api.get('/admin/committees/stats', { params }),
  // Members
  listMembers: (committeeId) => api.get(`/admin/committees/${committeeId}/members`),
  addMember: (committeeId, data) => api.post(`/admin/committees/${committeeId}/members`, data),
  updateMember: (committeeId, membershipId, data) => api.put(`/admin/committees/${committeeId}/members/${membershipId}`, data),
  removeMember: (committeeId, membershipId) => api.delete(`/admin/committees/${committeeId}/members/${membershipId}`),
};

// Icon mapping
const TYPE_ICONS = {
  church_committee: Users,
  home_group: Home,
  board: Briefcase,
  youth_committee: Sparkles,
  women_committee: Heart,
  men_committee: Shield,
  finance_committee: DollarSign,
  property_committee: Building,
  custom: Settings
};

const CommitteesManagement = () => {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editingCommittee, setEditingCommittee] = useState(null);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [memberFormData, setMemberFormData] = useState({
    member_id: '',
    member_name: '',
    member_email: '',
    position_id: 'member'
  });

  const [formData, setFormData] = useState({
    name: '',
    committee_type: 'church_committee',
    custom_type_name: '',
    church_id: '',
    description: '',
    meeting_schedule: '',
    meeting_location: '',
    is_active: true
  });

  // The units this admin covers — a committee belongs to one of them.
  const { data: churches } = useQuery({
    queryKey: ['committee-owner-churches'],
    queryFn: () => api.get('/admin/churches', { params: { limit: 500 } }).then(r => r.data)
  });
  const churchName = (id) => churches?.find(c => c.id === id)?.name || '';

  // Queries
  const { data: committees, isLoading } = useQuery({
    queryKey: ['committees'],
    queryFn: () => committeesAPI.list().then(r => r.data)
  });

  const { data: types } = useQuery({
    queryKey: ['committee-types'],
    queryFn: () => committeesAPI.getTypes().then(r => r.data)
  });

  const { data: positions } = useQuery({
    queryKey: ['committee-positions'],
    queryFn: () => committeesAPI.getPositions().then(r => r.data)
  });

  const { data: stats } = useQuery({
    queryKey: ['committee-stats'],
    queryFn: () => committeesAPI.getStats().then(r => r.data)
  });

  const { data: selectedCommitteeData, isLoading: loadingDetails } = useQuery({
    queryKey: ['committee', selectedCommittee],
    queryFn: () => committeesAPI.get(selectedCommittee).then(r => r.data),
    enabled: !!selectedCommittee
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => committeesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['committees']);
      queryClient.invalidateQueries(['committee-stats']);
      toast.success('Committee created successfully');
      handleCloseDialog();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create committee')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => committeesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['committees']);
      queryClient.invalidateQueries(['committee', selectedCommittee]);
      toast.success('Committee updated successfully');
      handleCloseDialog();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update committee')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => committeesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['committees']);
      queryClient.invalidateQueries(['committee-stats']);
      setSelectedCommittee(null);
      toast.success('Committee deleted successfully');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete committee')
  });

  const addMemberMutation = useMutation({
    mutationFn: ({ committeeId, data }) => committeesAPI.addMember(committeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['committee', selectedCommittee]);
      queryClient.invalidateQueries(['committees']);
      toast.success('Member added successfully');
      setShowMemberDialog(false);
      setMemberFormData({ member_id: '', member_name: '', member_email: '', position_id: 'member' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to add member')
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ committeeId, membershipId }) => committeesAPI.removeMember(committeeId, membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries(['committee', selectedCommittee]);
      queryClient.invalidateQueries(['committees']);
      toast.success('Member removed');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to remove member')
  });

  const handleOpenCreate = () => {
    setEditingCommittee(null);
    setFormData({
      name: '',
      committee_type: 'church_committee',
      custom_type_name: '',
      church_id: '',
      description: '',
      meeting_schedule: '',
      meeting_location: '',
      is_active: true
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (committee) => {
    setEditingCommittee(committee);
    setFormData({
      name: committee.name || '',
      committee_type: committee.committee_type || 'church_committee',
      custom_type_name: committee.custom_type_name || '',
      church_id: committee.church_id || '',
      description: committee.description || '',
      meeting_schedule: committee.meeting_schedule || '',
      meeting_location: committee.meeting_location || '',
      is_active: committee.is_active ?? true
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingCommittee(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Committee name is required');
      return;
    }
    
    if (editingCommittee) {
      updateMutation.mutate({ id: editingCommittee.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleAddMember = (e) => {
    e.preventDefault();
    if (!memberFormData.member_name.trim()) {
      toast.error('Member name is required');
      return;
    }
    
    addMemberMutation.mutate({
      committeeId: selectedCommittee,
      data: {
        ...memberFormData,
        member_id: memberFormData.member_id || `manual-${Date.now()}`
      }
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Committees</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage church committees, boards, and home group leadership
          </p>
        </div>
        <Button onClick={handleOpenCreate} data-testid="create-committee-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Committee
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_committees || 0}</p>
                <p className="text-sm text-slate-500">Total Committees</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_members || 0}</p>
                <p className="text-sm text-slate-500">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_position?.chairperson || 0}</p>
                <p className="text-sm text-slate-500">Chairpersons</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Home className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_type?.home_group || 0}</p>
                <p className="text-sm text-slate-500">Home Groups</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Committee List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">All Committees</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {committees?.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">
                    No committees yet. Create your first one!
                  </div>
                ) : (
                  committees?.map(committee => {
                    const Icon = TYPE_ICONS[committee.committee_type] || Users;
                    const isSelected = selectedCommittee === committee.id;
                    
                    return (
                      <div
                        key={committee.id}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between ${
                          isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        onClick={() => setSelectedCommittee(committee.id)}
                        data-testid={`committee-item-${committee.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <Icon className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{committee.name}</p>
                            <p className="text-xs text-slate-500">
                              {committee.member_count || 0} members
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Committee Details */}
        <div className="lg:col-span-2">
          {!selectedCommittee ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a committee to view details</p>
              </CardContent>
            </Card>
          ) : loadingDetails ? (
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-8 w-48 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ) : selectedCommitteeData ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{selectedCommitteeData.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {selectedCommitteeData.description || 'No description'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenEdit(selectedCommitteeData)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            if (window.confirm('Delete this committee?')) {
                              deleteMutation.mutate(selectedCommitteeData.id);
                            }
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="members">
                  <TabsList>
                    <TabsTrigger value="members">
                      Members ({selectedCommitteeData.member_count || 0})
                    </TabsTrigger>
                    <TabsTrigger value="details">Details</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="members" className="mt-4">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-slate-500">Committee members and positions</p>
                      <Button size="sm" onClick={() => setShowMemberDialog(true)}>
                        <UserPlus className="h-4 w-4 mr-1" />
                        Add Member
                      </Button>
                    </div>
                    
                    {selectedCommitteeData.members?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        No members yet. Add your first member!
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedCommitteeData.members?.map(member => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 bg-slate-200 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-slate-600">
                                  {member.member_name?.[0]?.toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{member.member_name}</p>
                                <p className="text-xs text-slate-500">
                                  {positions?.find(p => p.id === member.position_id)?.name || member.position_id}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => {
                                if (window.confirm(`Remove ${member.member_name}?`)) {
                                  removeMemberMutation.mutate({
                                    committeeId: selectedCommittee,
                                    membershipId: member.id
                                  });
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="details" className="mt-4 space-y-4">
                    {selectedCommitteeData.meeting_schedule && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">{selectedCommitteeData.meeting_schedule}</span>
                      </div>
                    )}
                    {selectedCommitteeData.meeting_location && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">{selectedCommitteeData.meeting_location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Building className="h-4 w-4 text-slate-400" />
                      <span className="text-sm" data-testid="committee-owner-unit">
                        {churchName(selectedCommitteeData.church_id) || 'No unit — platform wide'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedCommitteeData.is_active ? 'default' : 'secondary'}>
                        {selectedCommitteeData.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline">
                        {types?.find(t => t.id === selectedCommitteeData.committee_type)?.name || selectedCommitteeData.committee_type}
                      </Badge>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Create/Edit Committee Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCommittee ? 'Edit Committee' : 'Create New Committee'}
            </DialogTitle>
            <DialogDescription>
              {editingCommittee ? 'Update committee details' : 'Add a new committee to your organization'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Committee Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Church Committee"
                data-testid="committee-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="type">Committee Type</Label>
              <Select
                value={formData.committee_type}
                onValueChange={(val) => setFormData({ ...formData, committee_type: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types?.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="committee-church">Belongs to</Label>
              <Select
                value={formData.church_id || 'none'}
                onValueChange={(val) => setFormData({ ...formData, church_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger data-testid="committee-church-select">
                  <SelectValue placeholder="Choose a church or province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No unit — platform wide</SelectItem>
                  {churches?.map(church => (
                    <SelectItem key={church.id} value={church.id}>
                      {church.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Only staff with authority over this unit will see the committee.
              </p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the committee's purpose"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schedule">Meeting Schedule</Label>
                <Input
                  id="schedule"
                  value={formData.meeting_schedule}
                  onChange={(e) => setFormData({ ...formData, meeting_schedule: e.target.value })}
                  placeholder="e.g., Every Sunday after service"
                />
              </div>
              <div>
                <Label htmlFor="location">Meeting Location</Label>
                <Input
                  id="location"
                  value={formData.meeting_location}
                  onChange={(e) => setFormData({ ...formData, meeting_location: e.target.value })}
                  placeholder="e.g., Conference Room"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="active">Active</Label>
                <p className="text-xs text-slate-500">Committee is currently active</p>
              </div>
              <Switch
                id="active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Committee Member</DialogTitle>
            <DialogDescription>Add a new member to this committee</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddMember} className="space-y-4">
            <div>
              <Label htmlFor="member_name">Member Name *</Label>
              <Input
                id="member_name"
                value={memberFormData.member_name}
                onChange={(e) => setMemberFormData({ ...memberFormData, member_name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            
            <div>
              <Label htmlFor="member_email">Email (optional)</Label>
              <Input
                id="member_email"
                type="email"
                value={memberFormData.member_email}
                onChange={(e) => setMemberFormData({ ...memberFormData, member_email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="position">Position</Label>
              <Select
                value={memberFormData.position_id}
                onValueChange={(val) => setMemberFormData({ ...memberFormData, position_id: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {positions?.map(pos => (
                    <SelectItem key={pos.id} value={pos.id}>
                      {pos.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMemberDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addMemberMutation.isPending}>
                {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommitteesManagement;
